import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';

import moment from 'moment-timezone';
import { Subscription, catchError, of, take, throwError } from 'rxjs';

import { AccountQuotaUsageQuery, CuratorQuotaResponse } from '../../../api/agents/models/curation-agent.api';
import { CurationAgentsApiService } from '../../../api/agents/curation-agents.api.service';
import { GaugeChartModel } from '../../../Common/Charts/models/gaugeChart/gauge-chart.model';
import { GaugeChartOptionsModel } from '../../../Common/Charts/models/gaugeChart/gauge-chart-options.model';
import { SeriesModel } from '../../../Common/Charts/models/series.model';
import { ButtonTypesEnum } from '../../../Common/Forms/enums/button-types.enum';
import { messageSeverityEnum } from '../../../Common/Misc/enums/message-severity.enum';
import { DropdownOptionModel } from '../../../Common/Forms/models/dropdown-option.model';
import { SignificantDigitsPipe } from '../../../Common/Misc/pipes/SignificantDigits/significant-digits.pipe';
import { LocalizationService } from '../../../Core/Localization/services/localization.service';
import { CurrentUserStateService } from '@Core/Users/services/current-user-state.service';

/**
 * Which quota window the dashboard is reporting on. The quota endpoint's `period` param is lower
 * case, so map through {@link quotaQueryPeriods} rather than sending these values as they are.
 */
export enum AgentUsagePeriodEnum {
    current = 'CURRENT',
    previous = 'PREVIOUS'
}

/** What the quota endpoint answers with when the window asked for predates the pool */
const badRequestStatus: number = 400;

/** Wire value the quota endpoint expects for each window */
export const quotaQueryPeriods: Record<AgentUsagePeriodEnum, AccountQuotaUsageQuery['period']> = {
    [AgentUsagePeriodEnum.current]: 'current',
    [AgentUsagePeriodEnum.previous]: 'previous'
};

/**
 * Right hand panel on the management page: how much of the account's agent quota has been spent.
 * The pool is account-wide rather than per agent, matching `GET /api/v1/agents/curator/quota`.
 */
@Component({
    selector: 'agpr-agent-usage-dashboard',
    templateUrl: './agent-usage-dashboard.component.html',
    styleUrls: ['./agent-usage-dashboard.component.scss'],
    providers: [SignificantDigitsPipe],
    standalone: false
})
export class AgentUsageDashboardComponent implements OnInit, OnChanges, OnDestroy {
    readonly buttonTypes = ButtonTypesEnum;
    readonly messageSeverity = messageSeverityEnum;

    /** False inside the expanded dialog, which supplies the title row itself */
    @Input() showHeader: boolean = true;

    /** The arrow opens the dashboard out rather than hiding it */
    @Output() expanded: EventEmitter<void> = new EventEmitter<void>();
    /** The window the panel is reporting on, for whatever is shown beside it */
    @Output() periodSelected: EventEmitter<AgentUsagePeriodEnum> = new EventEmitter<AgentUsagePeriodEnum>();

    readonly periodOptions: DropdownOptionModel[] = [
        new DropdownOptionModel('AutomationAgents.usage.period.current', AgentUsagePeriodEnum.current),
        new DropdownOptionModel('AutomationAgents.usage.period.previous', AgentUsagePeriodEnum.previous)
    ];
    /** Which window to open on. Carried over when the panel is expanded into its own dialog */
    @Input() selectedPeriod: AgentUsagePeriodEnum = AgentUsagePeriodEnum.current;

    dailyAverageUsageChart: GaugeChartModel;

    totalQuota: string = '0';
    quotaUsed: string = '0';
    quotaUsedPercentage: string = '0%';
    daysRemaining: number = 0;
    quotaResetDate: string = '';
    dateFormat: string;

    loading: boolean = true;
    /** The window asked for does not exist yet, so there is nothing to report rather than an error */
    periodUnavailable: boolean = false;

    private quota: CuratorQuotaResponse;
    private readonly subscriptions: Subscription[] = [];

    constructor(
        private curationAgentsApi: CurationAgentsApiService,
        private significantDigits: SignificantDigitsPipe,
        private localizationService: LocalizationService,
        private userStateService: CurrentUserStateService
    ) {
        this.subscriptions.push(
            this.userStateService.getPreferences().subscribe(prefs => {
                this.dateFormat = prefs.getDateFormatString(true, false, false);
            })
        );
    }

    ngOnInit(): void {
        // Announced at once, so anything reporting alongside the panel starts on the same window
        this.periodSelected.emit(this.selectedPeriod);
        this.loadQuota();
    }

    /** The window can be handed in from outside - by the dialog this panel is expanded into */
    ngOnChanges(changes: SimpleChanges): void {
        if (changes.selectedPeriod && !changes.selectedPeriod.firstChange) {
            this.loadQuota();
        }
    }

    periodChanged(period: AgentUsagePeriodEnum): void {
        this.selectedPeriod = period;
        this.periodSelected.emit(period);
        this.loadQuota();
    }

    private loadQuota(): void {
        this.loading = true;
        this.periodUnavailable = false;

        this.curationAgentsApi.getQuota({
            period: quotaQueryPeriods[this.selectedPeriod]
        }).pipe(
            take(1),
            // An account whose pool is younger than a full period has no previous window to report
            // on, and the endpoint says so with a 400 rather than an empty one
            catchError((error: HttpErrorResponse) => {
                if (this.isPreviousPeriod && error?.status === badRequestStatus) {
                    this.periodUnavailable = true;

                    return of(null);
                }

                return throwError(() => error);
            })
        ).subscribe(quota => {
            this.loading = false;

            if (!quota) {
                this.dailyAverageUsageChart = null;

                return;
            }

            this.quota = quota;
            this.buildStats();
            this.buildChart();
        });
    }

    /** Whether the dashboard is reporting on a window that has already closed */
    private get isPreviousPeriod(): boolean {
        return this.selectedPeriod === AgentUsagePeriodEnum.previous;
    }

    /**
     * Mentions spent in the window being reported on. `count` is the pool's own running total, so
     * it only speaks for the current period - a closed one is carried on the usage response.
     */
    private get periodCount(): number {
        return this.isPreviousPeriod ? this.quota.usage?.total ?? 0 : this.quota.count;
    }

    /**
     * The limit actually enforced: the monthly allowance plus whatever extra support has granted
     * for this period. The response carries the sum, but it is derived here too for a pool that
     * predates that field.
     */
    private get effectiveLimit(): number {
        return this.quota.totalLimit ?? (this.quota.monthlyLimit ?? 0) + (this.quota.additionalQuota ?? 0);
    }

    private buildStats(): void {
        const limit = this.effectiveLimit;
        const used = this.periodCount;

        this.totalQuota = this.significantDigits.transform(limit);
        this.quotaUsed = this.significantDigits.transform(used);
        this.quotaUsedPercentage = limit === 0 ? '0%' : `${Math.round(used / limit * 100)}%`;
        // A closed window has nothing left to run down, and it reset on the day this one started
        this.daysRemaining = this.isPreviousPeriod
            ? 0
            : Math.max(0, moment(this.quota.nextResetDate).diff(moment(), 'days'));
        this.quotaResetDate = moment(this.isPreviousPeriod ? this.quota.windowStart : this.quota.nextResetDate)
            .utc()
            .format(this.dateFormat);
    }

    /**
     * The gauge tracks the daily pace rather than the raw total: how the average spent per day so far
     * compares to the daily allowance, so a full-looking gauge means the account is burning too fast.
     */
    private buildChart(): void {
        this.dailyAverageUsageChart = null;

        const usage = this.quota.usage;
        const windowStart = this.isPreviousPeriod ? usage?.start : this.quota.windowStart;
        const windowEnd = this.isPreviousPeriod ? usage?.end : this.quota.nextResetDate;

        const windowDays = Math.max(1, moment(windowEnd).diff(moment(windowStart), 'days'));
        // A closed window ran its full length; the open one has only run so far
        const elapsedDays = this.isPreviousPeriod
            ? windowDays
            : Math.max(1, moment().diff(moment(this.quota.windowStart), 'days'));
        const dailyAllowance = this.effectiveLimit / windowDays;
        const dailyUsed = this.periodCount / elapsedDays;
        const percentUsed = dailyAllowance === 0 ? 0 : (dailyUsed / dailyAllowance) * 100;

        const series: SeriesModel[] = [
            {
                label: '',
                dataProvider: of([
                    {
                        label: this.localizationService.transform('AutomationAgents.usage.dailyAverage'),
                        xValue: percentUsed
                    }
                ])
            }
        ];

        const options: GaugeChartOptionsModel = {
            minXDegrees: -210,
            maxXDegrees: 30,
            percentLabelOptions: { fontSize: 25, dy: -15 },
            seriesLabelOptions: { fontSize: 14, dy: 30, textAlign: 'middle' }
        };

        this.dailyAverageUsageChart = new GaugeChartModel(series, options);
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }
}
